import { joiResolver } from "@hookform/resolvers/joi";
import React, { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import InputMask from "react-input-mask";
import { useDispatch, useSelector } from "react-redux";

import IsCleaner from "../../HOC/IsCleaner";
import ModalPreviewSelectedPhoto from "../../components/ModalPreviewSelectedPhoto/ModalPreviewSelectedPhoto";
import CustomDropdown from "../../components/UI/CustomDropdown/CustomDropdown";
import { formatPhoneNumber } from "../../constants/formatPhoneNumber";
import { formatPhoneNumberToMask } from "../../constants/formatPhoneNumberToMask";
import avatar from '../../constants/images/avatar.svg';
import { compressImage } from "../../utils/imageCompression";
import copyIcon from "../../constants/images/copy-icon.svg";
import paymentMethod from '../../constants/images/payment-method.svg';
import upload from '../../constants/images/upload-icon.svg';
import { resolveMediaUrl } from "../../configs/urls";
import { citiesService } from "../../services/cities.service";
import { cleanerService } from "../../services/cleaner.service";
import { cleanerDetailsActions } from "../../store/slices/cleanerDetailsSlice";
import { modalPreviewSelectedPhotoActions } from "../../store/slices/modalPreviewSelectedPhotoSlice";
import { editProfileCleanerValidator } from "../../validators/editProfileCleaner.validator";
import styles from './SettingsPageСleaner.module.scss';

const SettingsPageLeaner = () => {
    const dispatch = useDispatch();
    const {isPreviewOpen} = useSelector(state => state.modalPreviewSelectedPhoto);
    const {cleaner} = useSelector(state => state.cleanerDetails);

    const [isCopiedLink, setIsCopiedLink] = useState("");
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
    const [city, setCity] = useState(cleaner?.city);

    // Active (serviceable) cities managed by the admin, fetched from the public
    // /cities/ endpoint. The city `name` is used as the value so it stays
    // compatible with the text `city` field stored on the backend.
    const [cities, setCities] = useState([]);
    const [isLoadingCities, setIsLoadingCities] = useState(true);

    useEffect(() => {
        let active = true;

        const fetchCities = async () => {
            try {
                const {data} = await citiesService.getActive();
                const list = Array.isArray(data) ? data : (data?.results || []);
                const names = list.map(item => item?.name).filter(Boolean);
                if (active) setCities(names);
            } catch (error) {
                console.error('Failed to load serviceable cities:', error);
                if (active) setCities([]);
            } finally {
                if (active) setIsLoadingCities(false);
            }
        };

        fetchCities();

        return () => {
            active = false;
        };
    }, []);


    const handleCreateFormData = (photo, user_data, cleaner_related_data) => {
        let formData = new FormData();

        //update profile photo
        if (photo) {
            formData.append('photos_data', photo);
        }

        formData.append('user_data', JSON.stringify(user_data));
        formData.append('cleaner_related_data', JSON.stringify(cleaner_related_data));

        return formData;
    };

    // default values from db for inputs
    useEffect(() => {
        setValue('first_name', cleaner.user.first_name);
        setValue('last_name', cleaner.user.last_name);
        setValue('email', cleaner.user.email);
        setValue('mobile_nummer', formatPhoneNumberToMask(cleaner.user.mobile_nummer));
        setValue('taxpayer_number', cleaner.taxpayer_number);
        if (cleaner.credit_cart_number) {
            setValue('credit_cart_number', cleaner.credit_cart_number);
        }
    }, [cleaner])

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
        setValue,
    } = useForm({
        resolver: joiResolver(editProfileCleanerValidator),
        mode: 'all',
    });

    const onSubmit = async (data) => {
        try {
            // updating those data which !== '' and !== null
            const filledData = Object.fromEntries(
                Object.entries(data).filter(([key, value]) => value !== '' && value !== null)
            );

            const {credit_cart_number, taxpayer_number, ...user} = filledData;

            const cleaner_related_data = {
                taxpayer_number: +taxpayer_number,
                credit_cart_number,
                city,
            };
            const user_data = {
                ...user,
                mobile_nummer: formatPhoneNumber(user?.mobile_nummer),
            }

            const formData = handleCreateFormData(profilePhoto, user_data, cleaner_related_data);

            toast.promise(
                cleanerService.updateById(cleaner?.id, formData),
                {
                    loading: "Оновлення даних...",
                    success: ({data}) => {
                        dispatch(cleanerDetailsActions.setCleaner({...data}));
                        setProfilePhotoPreview(null);
                        setProfilePhoto(null);
                        return "Ваші дані були успішно змінені";
                    },
                    error: "На жаль, не вдалось змінити дані. Спробуйте ще раз!",
                }
            , {id: "update-cleaner"}).catch((error) => {
                console.error("Error updating data:", error);
            });
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        return () => {
            toast.remove("update-cleaner");
        }
    }, []);

    const handleOpenFileExplorer = () => {
        const fileInput = document.getElementById('fileInput');

        if (fileInput) {
            fileInput.click();
        }
    }

    const handleUploadPhoto = async (e) => {
        const file = e.target.files[0];

        if (file) {
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Дозволені тільки файли типу PNG, JPG, JPEG');
                return;
            }

            const maxSize = 20 * 1024 * 1024; // 20 МБ в байтах
            if (file.size > maxSize) {
                toast.error('Максимальний розмір файлу - 20 МБ');
                return;
            }

            // Shrink in the browser before upload; falls back to the original.
            const compressed = await compressImage(file);
            const photoUrl = URL.createObjectURL(compressed);
            setProfilePhotoPreview(photoUrl);
            setProfilePhoto(compressed);
        }
    };

    const handleSelectPhotoForPreview = () => {
        if (profilePhotoPreview || cleaner?.user?.photos[0]) {
            dispatch(modalPreviewSelectedPhotoActions.openPreview('SettingsPageCleaner'));
        }
    };

    const handleCopyToClipboard = (url, role) => {
        navigator.clipboard.writeText(url)
          .then(() => {
              setIsCopiedLink(role);
          })
          .catch(err => {
              console.error("Error copying text:", err);
          });
    }

    return (
        <div className={styles.settingsPage_wrapper}>
            <div className={styles.settingsPage}>
                <h2 className={styles.settingsPage_title}>Параметри</h2>

                <div className={styles.settingsPage_settings}>
                    <div className={styles.settingsPage_settings_uploadPhoto}>
                        <div className={styles.settingsPage_settings_uploadPhoto_avatar}>
                            <img
                                onClick={handleSelectPhotoForPreview}
                                src={profilePhotoPreview || resolveMediaUrl(cleaner.user.photos[0]) || avatar}
                                alt="user`s photo for profile"
                            />
                        </div>

                        <input
                            id={'fileInput'}
                            type="file"
                            style={{display: 'none'}}
                            onChange={handleUploadPhoto}
                        />
                        <div
                            onClick={handleOpenFileExplorer}
                            className={styles.settingsPage_settings_uploadPhoto_upload}
                        >
                            <img src={upload} alt="upload photo icon"/>
                            <p>Натисніть щоб завантажити нове фото</p>
                        </div>
                    </div>

                    <div className={styles.settingsPage_settings_formContainer}>
                        <h3>Інформація про Вас</h3>

                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className={styles.settingsPage_settings_formContainer_form}
                            action="#"
                        >
                            <div className={styles.settingsPage_settings_formContainer_form_block}>
                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Прізвище
                                        <input
                                            {...register('last_name')}
                                            className={styles.input}
                                            type="text"
                                        />
                                    </label>
                                    {errors.last_name &&
                                        <span className={styles.error}> {errors.last_name.message}</span>}
                                </div>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Ім’я
                                        <input
                                            {...register('first_name')}
                                            className={styles.input}
                                            type="text"
                                        />
                                    </label>
                                    {errors.first_name &&
                                        <span className={styles.error}> {errors.first_name.message}</span>}
                                </div>
                            </div>

                            <div className={styles.settingsPage_settings_formContainer_form_block}>
                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Email
                                        <input
                                            {...register('email')}
                                            className={styles.input}
                                            type="email"
                                        />
                                    </label>
                                    {errors.email && <span className={styles.error}> {errors.email.message}</span>}
                                </div>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Місто
                                        <CustomDropdown
                                            type={'cityForm'}
                                            data={cities}
                                            saveData={setCity}
                                            defaultOption={isLoadingCities && !cleaner?.city ? 'Завантаження міст...' : cleaner?.city}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className={styles.settingsPage_settings_formContainer_form_block}>
                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        ІПН
                                        <input
                                            {...register('taxpayer_number')}
                                            className={styles.input}
                                            type="text"
                                        />
                                    </label>
                                    {errors.taxpayer_number &&
                                        <span className={styles.error}> {errors.taxpayer_number.message}</span>}
                                </div>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Номер телефону
                                        <InputMask
                                            className={styles.input}
                                            {...register('mobile_nummer')}
                                            mask="+38 (099) 999-99-99"
                                        />
                                    </label>
                                    {errors.mobile_nummer &&
                                        <span className={styles.error}> {errors.mobile_nummer.message}</span>}
                                </div>
                            </div>

                            <div className={styles.settingsPage_settings_formContainer_form_passwords}>
                                <p className={styles.settingsPage_settings_formContainer_form_passwords_title}>Змінити
                                    пароль</p>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Пароль
                                        <input
                                            {...register('password')}
                                            className={styles.input}
                                            type="password"
                                        />
                                    </label>
                                    {errors.password &&
                                        <span className={styles.error}> {errors.password.message}</span>}
                                </div>

                                <div className={styles.labelContainer}>
                                    <label className={styles.label}>
                                        Повторити пароль
                                        <input
                                            {...register('confirm_password')}
                                            className={styles.input}
                                            type="password"
                                        />
                                    </label>
                                    {errors.confirm_password &&
                                        <span className={styles.error}> {errors.confirm_password.message}</span>}
                                </div>
                            </div>

                            <div className={styles.settingsPage_settings_formContainer_form_payment}>
                                <p className={styles.settingsPage_settings_formContainer_form_payment_title}>Редагувати
                                    платіжні дані</p>
                                <div className={styles.settingsPage_settings_formContainer_form_payment_card}>
                                    <img src={paymentMethod} alt="payment method icon"/>
                                    <label className={styles.label}>
                                        Введіть дані картки
                                        {/*<input*/}
                                        {/*    {...register('credit_cart_number')}*/}
                                        {/*    className={`${styles.input} ${styles.card}`}*/}
                                        {/*    type="text"*/}
                                        {/*/>*/}
                                        <InputMask
                                            className={`${styles.input} ${styles.card}`}
                                            {...register('credit_cart_number')}
                                            mask="9999 9999 9999 9999"
                                        />
                                    </label>
                                    {errors.credit_cart_number &&
                                        <span className={styles.error}> {errors.credit_cart_number.message}</span>}
                                </div>
                            </div>

                            <div className={styles.settingsPage_settings_formContainer_form_payment}>
                                <p className={styles.settingsPage_settings_formContainer_form_payment_title}>Реферальне посилання</p>

                                <div className={styles.buttons}>
                                    <button
                                      onClick={() => handleCopyToClipboard(`${process.env.REACT_APP_URL}registration/cleaner?ref_id=${cleaner.user.id}`, "cleaner")}
                                      type={"button"} className={`${styles.copy} ${styles.cleaner}`}>
                                        <img src={copyIcon} alt="copy icon"/>
                                        {isCopiedLink === "cleaner" ? "Скопійовано!" : "Для клінера"}
                                    </button>

                                    <button
                                      onClick={() => handleCopyToClipboard(`${process.env.REACT_APP_URL}registration/customer?ref_id=${cleaner.user.id}`, "customer")}
                                      type={"button"} className={`${styles.copy} ${styles.customer}`}>
                                        <img src={copyIcon} alt="copy icon"/>
                                        {isCopiedLink === "customer" ? "Скопійовано!" : "Для замовника"}
                                    </button>
                                </div>
                            </div>

                            <div className={styles.settingsPage_settings_formContainer_form_button}>
                                <button
                                    type={"submit"}
                                    // disabled={!isValid}
                                    className={styles.button}
                                >Зберегти
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {(isPreviewOpen && (profilePhotoPreview || cleaner?.user?.photos[0])) &&
                    <ModalPreviewSelectedPhoto
                        photo={profilePhotoPreview || resolveMediaUrl(cleaner.user.photos[0])}
                        component={'SettingsPageCleaner'}
                    />}

                {/* to appear hot toast notification */}
                <Toaster/>
            </div>
        </div>
    );
};

export default IsCleaner(SettingsPageLeaner);
